import React, { useState, useEffect } from "react";
import { X, CheckCircle, Download, Upload, AlertCircle, FileText, Loader2, Send, History, Check, ShieldCheck, ChevronRight, RefreshCw } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
    getExternalInclusionVerificationStatusService,
    downloadInclusionDocService,
    uploadSignedInclusionDocService
} from "../../../../services/users.services";
import { toast } from "react-toastify";

const ProgramInclusionVerificationModal = ({ onClose }) => {
    const queryClient = useQueryClient();
    const [isDownloading, setIsDownloading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [fileError, setFileError] = useState(null);


    // CSS Fallback for animation
    const animationStyles = `
        @keyframes custom-spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
        }
        .animate-spin-custom {
            animation: custom-spin 1s linear infinite !important;
        }
    `;

    const userId = localStorage.getItem("user_id");
    const programId = localStorage.getItem("program_id");
    const enrolId = localStorage.getItem("enrol_id");
    const categoryId = localStorage.getItem("category");
    const email = localStorage.getItem("email");
    const inclusionLink = localStorage.getItem("inclusion_link");

    const { data: verificationData, isLoading: isVerificationLoading, refetch, isRefetching } = useQuery({
        queryFn: () => getExternalInclusionVerificationStatusService({ enrol_id: enrolId, program_id: programId }),
        queryKey: ["inclusion-verification-status", enrolId, programId],
        enabled: !!enrolId && enrolId !== "undefined" && enrolId !== "null",
        refetchOnWindowFocus: true,
    });

    useEffect(() => {
        if (categoryId == "undefined" || categoryId == "null") {
            onClose();
        }
    }, []);

    // Reset upload success if backend status changes to rejected
    useEffect(() => {
        if (verificationData?.status == "not_applicable") {
            onClose();
        } else if (verificationData?.status === "rejected" || verificationData?.status === "not_downloaded") {
            setUploadSuccess(false);
        }
    }, [verificationData?.status]);


    const { mutate: downloadDoc } = useMutation({
        mutationFn: downloadInclusionDocService,
        onMutate: () => setIsDownloading(true),
        onSuccess: () => {
            setIsDownloading(false);
            toast.success("Document requested! You will receive it on your email shortly.");
            queryClient.invalidateQueries(["inclusion-verification-status", enrolId]);
        },
        onError: (error) => {
            setIsDownloading(false);
            toast.error("Failed to request document. Please try again.");
            console.error(error);
        }
    });

    const { mutate: uploadDoc } = useMutation({
        mutationFn: uploadSignedInclusionDocService,
        onMutate: () => setIsUploading(true),
        onSuccess: () => {
            setIsUploading(false);
            setUploadSuccess(true);
            toast.success("Document uploaded successfully!");
            queryClient.invalidateQueries(["inclusion-verification-status", enrolId]);
        },
        onError: (error) => {
            setIsUploading(false);
            toast.error("Failed to upload document. Please try again.");
            console.error(error);
        }
    });

    const handleDownload = () => {
        if (!verificationData?.static_document_url) {
            toast.error("Document link not found. Please contact support.");
            return;
        }
        window.open(verificationData.static_document_url, '_blank');
    };

    const handleFileChange = (e) => {
        setFileError(null);
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            const maxSize = 2 * 1024 * 1024; // 2 MB

            if (file.size > maxSize) {
                const errorMessage = "File size should not be bigger than 2 MB";
                toast.error(errorMessage);
                setFileError(errorMessage);
                e.target.value = null; // Reset the input
                setSelectedFile(null);
                return;
            }
            setSelectedFile(file);
        }
    };

    const handleUpload = () => {
        if (!selectedFile) return;
        const formData = new FormData();
        formData.append("document", selectedFile);
        formData.append("enrol_id", enrolId);
        formData.append("user_id", userId);
        formData.append("email", email);
        uploadDoc(formData);
    };

    const getLoadingMessage = () => {
        if (isDownloading) return "Processing your request...";
        if (isUploading) return "Uploading signed document...";
        if (isRefetching) return "Syncing your status...";
        return null;
    };

    const loadingMessage = getLoadingMessage();

    if (isVerificationLoading) {
        return (
            <div className="fixed inset-0 z-30 md:pl-64 pt-20 bg-gray-900/40 backdrop-blur-sm flex items-center justify-center">
                <div className="bg-white p-8 rounded-3xl shadow-2xl flex flex-col items-center gap-4">
                    <Loader2 className="w-10 h-10 text-blue-600 animate-spin-custom" />
                    <p className="text-sm font-bold text-gray-800 uppercase tracking-widest animate-pulse">Initializing Portal...</p>
                </div>
            </div>
        );
    }

    if (verificationData?.status === "accepted") return null;

    const status = verificationData?.status || "not_downloaded";
    const isUnderReview = status === "under_review" || (uploadSuccess && status !== "rejected");


    return (
        <div className="fixed inset-0 z-30 md:pl-64 pt-20 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity duration-300" />

            {/* Modal Container */}
            <div className="relative bg-white w-full max-w-3xl h-fit max-h-[95vh] flex flex-col overflow-hidden rounded-2xl shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-300">

                {/* Fixed Header */}
                <div className="sticky top-0 z-50 flex justify-between items-center px-6 py-4 bg-white border-b border-gray-50">
                    <div className="flex items-center gap-3">
                        <div className="flex w-9 h-9 rounded-xl bg-blue-50 items-center justify-center text-blue-600">
                            <FileText className="w-5 h-5" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-800 leading-none mb-1">Program Inclusion</h2>
                            {/* <div className="flex items-center gap-1.5">
                                <span className={`w-2 h-2 rounded-full ${status === 'not_downloaded' ? 'bg-orange-400' : 'bg-blue-500 animate-pulse'}`} />
                                <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest leading-none">
                                    {status === 'not_downloaded' ? 'Verification Step 1' : 'Verification Step 2'}
                                </p>
                            </div> */}
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => refetch()}
                            disabled={isRefetching}
                            className="p-2 hover:bg-gray-50 rounded-lg transition-all active:scale-95 text-gray-400 hover:text-blue-500 group flex items-center gap-2"
                            title="Refresh Status"
                        >
                            <span className="text-[10px] font-bold uppercase tracking-wider hidden md:block">Check for Updates</span>
                            <RefreshCw className={`w-4 h-4 ${isRefetching ? 'animate-spin-custom' : ''}`} />
                        </button>
                    </div>
                </div>

                {/* Main Content */}
                <div className="flex-1 overflow-y-auto bg-gray-50/50 px-4 md:px-6 py-4 transition-all duration-500">
                    <div className="">
                        {!isUnderReview ? (
                            <div className="space-y-3">
                                {/* Header Info */}
                                <div className="text-center space-y-1">
                                    <h3 className="text-xl font-bold text-gray-900">Last Step to Activate Your LMS</h3>
                                    <p className="text-sm text-gray-500 font-medium max-w-md mx-auto">
                                        Please download the program inclusion specification, sign it, and upload it here.
                                    </p>
                                </div>

                                {/* Step 1: Download Card */}
                                <div className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4 group hover:border-blue-200 transition-all">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600 group-hover:scale-110 transition-transform">
                                            <Download className="w-5 h-5" />
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-gray-800 text-sm">1. Download Specification</h4>
                                            <p className="text-[10px] text-gray-400 font-medium">Read carefully & digitally sign.</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={handleDownload}
                                        className="w-full md:w-auto px-4 py-2 bg-gray-900 text-white rounded-lg font-bold text-xs flex items-center justify-center gap-2 hover:bg-gray-800 active:scale-95 transition-all shadow-md"
                                    >
                                        <Download className="w-3 h-3" />
                                        Download PDF
                                    </button>
                                </div>

                                {/* Step 2: Upload Card */}
                                <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm space-y-3">
                                    <div className="flex items-center gap-3 mb-1">
                                        <div className="w-8 h-8 bg-green-50 rounded-lg flex items-center justify-center text-green-600">
                                            <Upload className="w-4 h-4" />
                                        </div>
                                        <h4 className="font-bold text-gray-800 text-sm">2. Upload Signed Document</h4>
                                    </div>

                                    {status === "rejected" && (
                                        <div className="p-3 bg-red-50/50 border border-red-100 rounded-lg flex items-start gap-2 animate-in slide-in-from-top-2">
                                            <AlertCircle className="w-4 h-4 text-red-500 mt-0.5" />
                                            <div>
                                                <h4 className="font-bold text-red-900 text-xs uppercase tracking-tight">Re-upload Needed</h4>
                                                <p className="text-[11px] text-red-700 font-semibold mt-0.5">
                                                    {verificationData?.rejected_reason || "Signature not clear."}
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    <label className="block group cursor-pointer">
                                        <input type="file" onChange={handleFileChange} accept=".pdf" className="hidden" />
                                        <div className={`flex items-center justify-center gap-4 py-4 px-4 border-2 border-dashed rounded-xl transition-all duration-300 ${fileError
                                            ? 'border-red-500 bg-red-50/40'
                                            : selectedFile
                                                ? 'border-green-500 bg-green-50/40'
                                                : 'border-gray-200 bg-gray-50/20 group-hover:border-blue-500 group-hover:bg-blue-50/40'
                                            }`}>
                                            <div className={`p-3 rounded-lg transition-all duration-300 flex-shrink-0 ${fileError
                                                ? 'text-red-500 border border-red-500 bg-white shadow-md'
                                                : selectedFile
                                                    ? 'text-green-500 border border-green-500 bg-white shadow-md'
                                                    : 'bg-white text-gray-400 group-hover:text-blue-600 shadow-sm border border-gray-100'
                                                }`}>
                                                {fileError ? <AlertCircle className="w-5 h-5" /> : selectedFile ? <Check className="w-5 h-5" /> : <Upload className="w-5 h-5" />}
                                            </div>
                                            <div className="flex flex-col min-w-0 flex-1">
                                                <p className={`font-bold text-xs truncate ${fileError ? 'text-red-700' : selectedFile ? 'text-green-700' : 'text-gray-700'}`}>
                                                    {fileError ? "Invalid File" : selectedFile ? selectedFile.name : "Upload signed PDF"}
                                                </p>
                                                <p className={`text-[10px] font-bold uppercase truncate tracking-widest mt-0.5 ${fileError ? 'text-red-400' : 'text-gray-400'}`}>
                                                    {fileError ? fileError : selectedFile ? `${Math.round(selectedFile.size / 1024)} KB • Ready` : "Max 2MB"}
                                                </p>
                                            </div>
                                        </div>
                                    </label>

                                    <button
                                        disabled={!selectedFile || isUploading}
                                        onClick={handleUpload}
                                        className="w-full py-3 rounded-lg font-bold text-xs flex items-center justify-center gap-2 transition-all bg-blue-600 text-white hover:bg-blue-700 active:scale-[0.98] disabled:bg-gray-100 disabled:text-gray-300"
                                    >
                                        {isUploading ? <Loader2 className="w-4 h-4 animate-spin-custom" /> : <Send className="w-3 h-3" />}
                                        {isUploading ? "Uploading..." : "Confirm & Submit"}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="bg-white p-12 rounded-3xl border border-gray-100 shadow-sm text-center space-y-6 animate-in zoom-in-95 duration-300">
                                <div className="w-20 h-20 bg-green-500 text-white rounded-full flex items-center justify-center mx-auto shadow-2xl shadow-green-100 relative">
                                    <CheckCircle className="w-10 h-10" />
                                    <div className="absolute inset-0 rounded-full bg-green-500 animate-ping opacity-20" />
                                </div>
                                <div className="space-y-3">
                                    <h3 className="text-2xl font-bold text-gray-800 uppercase tracking-tight">Document Received</h3>
                                    <p className="text-base text-gray-600 font-medium max-w-sm mx-auto leading-relaxed">
                                        Your signed Program Inclusion Specification document has been submitted successfully. Please wait 24-48 hours for verification. Once approved, your dashboard will be activated.
                                    </p>
                                </div>
                                <div className="flex flex-col items-center gap-4 pt-4 border-t border-gray-50">
                                    <div className="inline-flex items-center gap-3 px-6 py-2.5 bg-blue-50 text-blue-700 rounded-full text-[13px] font-bold uppercase tracking-widest border border-blue-100">
                                        <Loader2 className={`w-4 h-4 ${isRefetching ? 'animate-spin-custom' : ''}`} />
                                        {isRefetching ? 'Synchronizing... ' : 'Verification in Progress'}
                                    </div>
                                    <button
                                        onClick={() => refetch()}
                                        disabled={isRefetching}
                                        className="text-sm font-bold text-blue-600 hover:text-blue-800 flex items-center gap-2 py-2.5 px-6 rounded-xl bg-white border border-blue-200 shadow-sm transition-all active:scale-95 disabled:opacity-50"
                                    >
                                        <RefreshCw className={`w-4 h-4 ${isRefetching ? 'animate-spin-custom' : ''}`} />
                                        Refresh Verification Status
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

            </div>
            {/* Loading Overlay */}
            {loadingMessage && (
                <div className="absolute inset-0 z-[1000] bg-white/80 backdrop-blur-[2px] flex flex-col items-center justify-center transition-all duration-300">
                    <div className="bg-white p-10 rounded-3xl shadow-2xl flex flex-col items-center gap-6 border border-gray-100 animate-in zoom-in-95 duration-200">
                        <div className="relative">
                            <div className="w-20 h-20 border-4 border-blue-50 rounded-full" />
                            <Loader2 className="w-20 h-20 text-blue-600 animate-spin-custom absolute top-0 left-0" />
                        </div>
                        <div className="text-center">
                            <p className="text-base font-bold text-gray-800 uppercase tracking-widest">{loadingMessage}</p>
                            <p className="text-[11px] text-gray-400 font-bold mt-2 uppercase">Please do not refresh or close this window</p>
                        </div>
                    </div>
                </div>
            )}
            <style>{animationStyles}</style>
        </div>
    );
};

export default ProgramInclusionVerificationModal;
